/**
 * PieMenu — Blender-style radial menu.
 *
 * Opens at a cursor anchor with N equally-spaced sectors. Three commit modes:
 *   1. Tap-and-flick: press the opener key, move the cursor past the dead
 *      zone, release the key — the sector under the cursor angle commits.
 *   2. Tap-and-release: press the opener key, release without moving — the
 *      ring stays open and a subsequent click inside a sector commits.
 *   3. Digit keys: while open, 1..N commits by index.
 *
 * Escape or right-click cancels. Clicking outside any sector also cancels.
 *
 * When the anchor is near a viewport edge the ring is clamped inward so every
 * sector remains fully visible. Angle math uses the clamped ring center.
 */

import type { CSSProperties, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { GLASS_PANEL } from "./styles.ts";
import {
  COLOR_ACCENT,
  COLOR_ACTIVE_BG,
  COLOR_BORDER,
  COLOR_BORDER_STRONG,
  COLOR_SURFACE,
  COLOR_TEXT,
  COLOR_TEXT_MUTED,
  FONT_SIZE_CAPTION,
  FONT_SIZE_TINY,
  RADIUS_LARGE,
  RADIUS_XS,
  Z_POPOVER,
} from "./tokens.ts";

const BASE_RADIUS = 92;
const DEAD_ZONE = 20;
const EDGE_MARGIN = 16;
const SECTOR_W = 108;
const SECTOR_MIN_H = 32;
const CENTER_GLYPH = 22;
const ENTER_MS = 140;
// Minimum gap between adjacent sector button edges. Together with SECTOR_W
// this sets the chord required between adjacent centers, which drives the
// dynamic radius for larger option counts.
const CHORD_GAP = 14;

/**
 * Pie radius for `n` options — expands with n so adjacent buttons never
 * crowd. The chord between adjacent centers is `SECTOR_W + CHORD_GAP`.
 */
function pieRadiusFor(n: number): number {
  if (n < 2) return BASE_RADIUS;
  const chord = SECTOR_W + CHORD_GAP;
  return Math.max(BASE_RADIUS, chord / (2 * Math.sin(Math.PI / n)));
}

const STYLE_ID = "__pie-menu-style";

function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes pieMenuIn {
      from { opacity: 0; transform: scale(0.92); }
      to   { opacity: 1; transform: scale(1); }
    }
  `;
  document.head.appendChild(el);
}

export interface PieMenuOption {
  id: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
}

interface PieMenuProps {
  open: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
  anchorPoint: { x: number; y: number } | null;
  options: PieMenuOption[];
  /**
   * When set, releasing this key (by lowercased e.key) commits the active
   * sector while the ring is still in probe/flick mode. Gives Blender's
   * press-Z, flick, release-Z feel. Case-insensitive.
   */
  holdKey?: string;
  /**
   * Alternative to `holdKey` that matches against `e.code` instead of
   * `e.key`. Use for keys whose printed character varies by keyboard
   * layout (e.g. backtick: `Backquote` always fires regardless of locale).
   */
  holdCode?: string;
}

function clampCenter(
  anchor: { x: number; y: number },
  vw: number,
  vh: number,
  radius: number,
): { x: number; y: number } {
  const half = radius + Math.max(SECTOR_W, SECTOR_MIN_H) / 2 + EDGE_MARGIN;
  return {
    x: Math.max(half, Math.min(vw - half, anchor.x)),
    y: Math.max(half, Math.min(vh - half, anchor.y)),
  };
}

/**
 * Sector angle at index i for N options, starting at north and going clockwise.
 * Returns radians in the standard screen-space convention (y grows downward).
 */
function sectorAngle(i: number, n: number): number {
  return -Math.PI / 2 + (i * 2 * Math.PI) / n;
}

/** Sector index whose angular wedge contains the given screen-space angle. */
function angleToSector(angle: number, n: number): number {
  const step = (2 * Math.PI) / n;
  // Shift so that sector 0 (north) is centered on angle 0 for the modulo math.
  const shifted = angle + Math.PI / 2 + step / 2;
  const wrapped = ((shifted % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  return Math.floor(wrapped / step) % n;
}

export function PieMenu({
  open,
  onClose,
  onSelect,
  anchorPoint,
  options,
  holdKey,
  holdCode,
}: PieMenuProps) {
  const [center, setCenter] = useState<{ x: number; y: number } | null>(null);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [persistent, setPersistent] = useState(false);

  // Mutable refs used by the global listeners so we don't re-bind on every
  // activeIdx change (would miss rapid flicks).
  const modeRef = useRef<"probe" | "flick" | "persistent">("probe");
  const activeIdxRef = useRef<number | null>(null);
  const centerRef = useRef<{ x: number; y: number } | null>(null);
  const optionsRef = useRef<PieMenuOption[]>(options);
  const movedRef = useRef(false);
  const holdKeyReleasedRef = useRef(false);

  optionsRef.current = options;

  useEffect(() => {
    ensureStyle();
  }, []);

  // Reset and clamp on each open transition.
  useEffect(() => {
    if (!open || !anchorPoint) {
      setCenter(null);
      setActiveIdx(null);
      setPersistent(false);
      modeRef.current = "probe";
      activeIdxRef.current = null;
      movedRef.current = false;
      holdKeyReleasedRef.current = false;
      return;
    }
    const radius = pieRadiusFor(options.length);
    const c = clampCenter(
      anchorPoint,
      window.innerWidth,
      window.innerHeight,
      radius,
    );
    centerRef.current = c;
    setCenter(c);
    setActiveIdx(null);
    setPersistent(false);
    modeRef.current = "probe";
    activeIdxRef.current = null;
    movedRef.current = false;
    holdKeyReleasedRef.current = false;
  }, [open, anchorPoint, options.length]);

  // Global pointer/key listeners while open.
  useEffect(() => {
    if (!open || !center) return;

    const commit = (idx: number | null) => {
      if (idx == null) {
        onClose();
        return;
      }
      const opt = optionsRef.current[idx];
      if (!opt || opt.disabled) {
        onClose();
        return;
      }
      onSelect(opt.id);
    };

    const computeActive = (x: number, y: number): number | null => {
      const c = centerRef.current;
      if (!c) return null;
      const dx = x - c.x;
      const dy = y - c.y;
      if (Math.hypot(dx, dy) < DEAD_ZONE) return null;
      return angleToSector(Math.atan2(dy, dx), optionsRef.current.length);
    };

    const onMove = (e: PointerEvent) => {
      const next = computeActive(e.clientX, e.clientY);
      activeIdxRef.current = next;
      setActiveIdx(next);
      if (modeRef.current === "probe" && next != null) {
        modeRef.current = "flick";
        movedRef.current = true;
      }
    };

    const onUp = (e: PointerEvent) => {
      if (e.button === 2) return; // right-click handled by contextmenu
      if (modeRef.current === "flick") {
        commit(activeIdxRef.current);
        return;
      }
      // Probe → persistent if no movement yet. Persistent-mode clicks
      // are handled on the sector (commit) or the overlay (cancel); the
      // window pointerup is intentionally a no-op there.
      if (modeRef.current === "probe" && !movedRef.current) {
        modeRef.current = "persistent";
        setPersistent(true);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      // Digits 1..N select by index.
      const d = parseInt(e.key, 10);
      if (!Number.isNaN(d) && d >= 1 && d <= optionsRef.current.length) {
        e.preventDefault();
        e.stopPropagation();
        commit(d - 1);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      // Swallow everything else so global shortcuts don't fire while the
      // pie is open (matches Popover.tsx lines 304-351).
      e.stopPropagation();
    };

    const onKeyUp = (e: KeyboardEvent) => {
      const matchesKey =
        holdKey != null && e.key.toLowerCase() === holdKey.toLowerCase();
      const matchesCode = holdCode != null && e.code === holdCode;
      if (!matchesKey && !matchesCode) return;
      if (holdKeyReleasedRef.current) return;
      holdKeyReleasedRef.current = true;
      // Release-key-commits while still in probe/flick; if already persistent,
      // the user tapped without moving — leave the menu open for clicking.
      if (modeRef.current === "flick") {
        commit(activeIdxRef.current);
      } else if (modeRef.current === "probe") {
        if (activeIdxRef.current != null) {
          commit(activeIdxRef.current);
        } else {
          modeRef.current = "persistent";
          setPersistent(true);
        }
      }
    };

    const onContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };

    // In probe/flick mode the overlay is transparent to pointer events
    // (pointerEvents: "none"). Block pointerdown in capture phase so the
    // viewport never records a "down" position for the click — otherwise
    // the R3F handlePointerUp fires before our bubble-phase onUp and calls
    // handleEditClick, replacing the face selection with whatever is under
    // the cursor at commit time.
    const onPointerDownCapture = (e: PointerEvent) => {
      if (modeRef.current === "probe" || modeRef.current === "flick") {
        e.stopPropagation();
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointerdown", onPointerDownCapture, true);
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);
    window.addEventListener("contextmenu", onContextMenu, true);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointerdown", onPointerDownCapture, true);
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
      window.removeEventListener("contextmenu", onContextMenu, true);
    };
  }, [open, center, onSelect, onClose, holdKey, holdCode]);

  if (!open || !center) return null;

  const n = options.length;
  const radius = pieRadiusFor(n);
  const svgSize = radius * 2.5;
  const svgOffset = radius * 1.25;

  return createPortal(
    <div
      data-floating-portal
      onPointerDown={(e) => {
        // Sector onPointerDown stops propagation for exact hits. Anything
        // reaching here is either outside any sector or in the gap between
        // them — but still inside the pie's angular wedge counts as a
        // selection (Blender treats the wedge, not the button rect, as
        // the hit target).
        if (modeRef.current !== "persistent") return;
        e.stopPropagation();
        const idx = (() => {
          const c = centerRef.current;
          if (!c) return null;
          const dx = e.clientX - c.x;
          const dy = e.clientY - c.y;
          if (Math.hypot(dx, dy) < DEAD_ZONE) return null;
          return angleToSector(Math.atan2(dy, dx), optionsRef.current.length);
        })();
        if (idx == null) {
          onClose();
          return;
        }
        const opt = optionsRef.current[idx];
        if (!opt || opt.disabled) {
          onClose();
          return;
        }
        onSelect(opt.id);
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: Z_POPOVER + 1,
        pointerEvents: persistent ? "auto" : "none",
        // During flick we want the world-level listeners to see moves —
        // the overlay is transparent to pointer events until persistent.
      }}
    >
      {/* Ring container — animated, centered on the clamped anchor */}
      <div
        style={{
          position: "absolute",
          left: center.x,
          top: center.y,
          transform: "translate(-50%, -50%)",
          animation: `pieMenuIn ${ENTER_MS}ms cubic-bezier(.2,.8,.2,1)`,
          pointerEvents: "none",
        }}
      >
        {/* Dead-zone ring (subtle reference) */}
        <div
          style={{
            position: "absolute",
            left: -DEAD_ZONE,
            top: -DEAD_ZONE,
            width: DEAD_ZONE * 2,
            height: DEAD_ZONE * 2,
            borderRadius: "50%",
            border: `1px dashed ${COLOR_BORDER}`,
            opacity: 0.6,
          }}
        />

        {/* Active-sector connector (SVG line) */}
        {activeIdx != null && (
          <svg
            width={svgSize}
            height={svgSize}
            style={{
              position: "absolute",
              left: -svgOffset,
              top: -svgOffset,
              pointerEvents: "none",
            }}
          >
            <line
              x1={svgOffset}
              y1={svgOffset}
              x2={svgOffset + Math.cos(sectorAngle(activeIdx, n)) * radius}
              y2={svgOffset + Math.sin(sectorAngle(activeIdx, n)) * radius}
              stroke={COLOR_ACCENT}
              strokeWidth={1.5}
              opacity={0.5}
            />
          </svg>
        )}

        {/* Center glyph */}
        <div
          style={{
            position: "absolute",
            left: -CENTER_GLYPH / 2,
            top: -CENTER_GLYPH / 2,
            width: CENTER_GLYPH,
            height: CENTER_GLYPH,
            borderRadius: "50%",
            background: COLOR_SURFACE,
            border: `1px solid ${COLOR_BORDER_STRONG}`,
          }}
        />

        {/* Sector buttons */}
        {options.map((opt, i) => {
          const a = sectorAngle(i, n);
          const x = Math.cos(a) * radius;
          const y = Math.sin(a) * radius;
          const isActive = activeIdx === i;
          const base: CSSProperties = {
            position: "absolute",
            left: x,
            top: y,
            transform: `translate(-50%, -50%) scale(${isActive ? 1.04 : 1})`,
            transition: "transform 80ms, background 80ms, border-color 80ms",
            width: SECTOR_W,
            minHeight: SECTOR_MIN_H,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            padding: "4px 8px",
            paddingRight: 14,
            cursor: opt.disabled ? "not-allowed" : "pointer",
            pointerEvents: persistent ? "auto" : "none",
            opacity: opt.disabled ? 0.45 : 1,
            color: COLOR_TEXT,
            ...GLASS_PANEL,
            borderRadius: RADIUS_LARGE,
            ...(isActive
              ? {
                  background: COLOR_ACTIVE_BG,
                  border: `1px solid ${COLOR_ACCENT}`,
                }
              : {}),
          };
          return (
            <div
              key={opt.id}
              style={base}
              onPointerEnter={() => {
                if (modeRef.current === "persistent") {
                  activeIdxRef.current = i;
                  setActiveIdx(i);
                }
              }}
              onPointerDown={(e) => {
                if (!persistent) return;
                if (opt.disabled) {
                  e.stopPropagation();
                  return;
                }
                e.stopPropagation();
                onSelect(opt.id);
              }}
            >
              {opt.icon && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {opt.icon}
                </div>
              )}
              <div
                style={{
                  fontSize: FONT_SIZE_CAPTION,
                  lineHeight: 1.2,
                  flex: 1,
                  textAlign: "left",
                }}
              >
                {opt.label}
              </div>
              <div
                style={{
                  position: "absolute",
                  top: 1,
                  right: 3,
                  fontSize: FONT_SIZE_TINY,
                  color: COLOR_TEXT_MUTED,
                  borderRadius: RADIUS_XS,
                  padding: "0 2px",
                  lineHeight: "11px",
                }}
              >
                {i + 1}
              </div>
            </div>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}
